import { Check, Copy } from 'lucide-react';
import React from 'react';

import type { HandleType } from '@/components/carrier-directory/types';

export interface HandleData {
  id: string;
  handleType: HandleType;
  name: string;
  value?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  description?: string | null;
}

interface HandleCardProps {
  handle: HandleData;
  onCopy: (value: string, name: string) => void;
  onSelect: () => void;
  actions?: ('copy' | 'use')[];
}

const HANDLE_TYPE_ICONS: Record<HandleType, string> = {
  PHONE: '📞',
  EMAIL: '✉️',
  WEBSITE: '🌐',
  FAX: '📠',
  MAILING_ADDRESS: '🏠',
};

/**
 * Generic handle card component for displaying contact information
 * Works with any entity that has handles (carriers, providers, police depts)
 */
export const HandleCard: React.FC<HandleCardProps> = ({
  handle,
  onCopy,
  onSelect,
  actions = ['copy', 'use'],
}) => {
  const icon = HANDLE_TYPE_ICONS[handle.handleType] || '📄';

  const displayValue =
    handle.value ||
    [handle.addressLine1, handle.city, handle.state, handle.zip]
      .filter(Boolean)
      .join(', ');

  return (
    <div className="p-3 rounded-md border border-gray-200 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">{icon}</span>
            <span className="font-medium text-sm text-gray-900">
              {handle.name}
            </span>
          </div>
          <div className="text-sm text-gray-700 break-words">
            {displayValue}
          </div>
          {handle.description && (
            <div className="text-xs text-gray-500 mt-1 italic">
              {handle.description}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 flex-shrink-0">
          {actions.includes('copy') && (
            <button
              type="button"
              onClick={() => onCopy(displayValue, handle.name)}
              className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-3 h-3" />
            </button>
          )}
          {actions.includes('use') && (
            <button
              type="button"
              onClick={onSelect}
              className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors font-medium"
              title="Use this handle"
            >
              <Check className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
